import { DynamicHeightScrollArea } from "@/components/atoms/DynamicHeightScrollArea";
import { TooltipButton } from "@/components/atoms/TooltipButton";
import { AddMemberFormData, AddMemberModal } from "@/components/custom/AddMemberModal";
import DecisionModal from "@/components/custom/InfoPopups/DecisionModal";
import ErrorPopup from "@/components/custom/InfoPopups/ErrorPopup";
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import { NavigationBarBreadCrumbs } from '@/components/molecules/BackNav';
import { PageFooter } from "@/components/molecules/PageFooter";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CheckOrganizationAccess } from "@/features/rbac/components/molecules/CheckOrganizationAccess/CheckOrganizationAccess";
import { useInviteUserToOrganization } from "@/features/organization/hooks/useMutation/useInviteUserToOrganization";
import { useRemoveUserFromOrganization } from "@/features/organization/hooks/useMutation/useRemoveUserFromOrganization";
import { useTransferOrganizationOwnership } from "@/features/organization/hooks/useMutation/useTransferOrganizationOwnership";
import { useOrganizationStore } from "@/lib/zustand/useStore";
import { Loader, Trash2, UserStar } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";

export function ManageOrganizationMembers() {
    const { organization } = useOrganizationStore();
    const organizationMembers = organization?.organisation_members ?? [];
    console.log(organizationMembers)
    /* 
    * Remove user from organization
    */
    const { orgId } = useParams();
    const { mutateAsync: removeUserMutation, isPending: isPendingRemoveUserMutation } = useRemoveUserFromOrganization();
    const deleteHandler = (userId: string) => {
        console.log(userId)
        DecisionModal({
            title: "Remove user from organization",
            message: "Are you sure? This action cannot be undone.",
        }).then((decision) => {
            if (decision.isConfirmed) {
                removeUserMutation({
                    userId,
                    orgId: Number(orgId),
                })
                    .then(() => {
                        SuccessPopup("User removed from organization")
                    })
                    .catch((error) => {
                        console.error(error);
                        ErrorPopup("Couldn't delete user from organization")
                    })
            }
        })
    }
    /*
    * Transfer ownership of organization
    */
    const { mutateAsync: transferOrganizationOwnershipMutation, isPending: isPendingTransferOrganizationOwnershipMutation } = useTransferOrganizationOwnership();
    const transferOrganizationOwnershipHandler = (newOwnerId: string) => {
        DecisionModal({
            title: "Transfer ownership of organization",
            message: "Are you sure? This action cannot be undone.",
            confirmButtonText: "Transfer",
        }).then((decision) => {
            if (decision.isConfirmed) {
                transferOrganizationOwnershipMutation({
                    newOwnerId,
                    orgId: Number(orgId),
                })
                    .then(() => {
                        SuccessPopup("Ownership of organization transferred")
                    })
                    .catch((error) => {
                        console.error(error);
                        ErrorPopup("Couldn't transfer ownership of organization")
                    })
            }
        })
    }


    /* 
    * Invite user to organization
    */
    const inviteUserMutation = useInviteUserToOrganization();
    const [showAddMemberModal, setShowAddMemberModal] = useState(false);
    const submitHandler = (data: AddMemberFormData) => {
        inviteUserMutation.mutateAsync({
            email: data.email,
            orgId: Number(orgId),
        })
            .then((res) => {
                SuccessPopup(res?.data?.message || "User invited to organization");
                setShowAddMemberModal(false);
            })
            .catch((error) => {
                console.error(error);
                if (error instanceof Error) {
                    ErrorPopup(error.message)
                } else {
                    ErrorPopup("Couldn't invite user to organization")
                }
            })
    }

    return (
        <>
            <AddMemberModal open={showAddMemberModal} onOpenChange={setShowAddMemberModal} onSubmitHandler={submitHandler} />

            <NavigationBarBreadCrumbs.Set
                currentPageName="Manage Members / Invitations"
                pages={[
                    {
                        title: "Home",
                        goTo: `/welcome`
                    },
                    {
                        title: organization?.name || "-",
                        goTo: `/organization/${orgId}`
                    },

                ]}
            />

            <div className="flex items-stretch flex-1">
                <div className="flex flex-col flex-1 ">
                    <div className="flex flex-col flex-1">

                        <DynamicHeightScrollArea
                            offset={50}
                        >
                            <div className="flex flex-col gap-2">

                                {organizationMembers?.map((member) => (
                                    <div key={member.user?.id} className="flex items-center justify-between w-full px-3 bg-gray-100 border rounded-md gap-2 h-[48px]">
                                        <div className="flex items-center gap-2 grow">
                                            <div className="flex flex-col items-start max-w-[300px] grow">
                                                <span className="text-sm font-medium line-clamp-1">
                                                    {member.user?.first_name || ""} {member.user?.last_name || ""}
                                                </span>
                                                {(!member.user?.first_name && !member.user?.last_name) && (
                                                    <span className="text-xs italic font-thin line-clamp-1">
                                                        No name specified
                                                    </span>
                                                )}
                                                <span className="text-[11px] text-gray-500 line-clamp-1 italic"><span className="font-semibold">{member.user?.email ?? ""}</span></span>
                                            </div>
                                            <div className="flex flex-1">
                                                <span className={`text-xs text-start text-gray-500 line-clamp-3`}>{member.role?.label}</span>
                                            </div>
                                        </div>

                                        <div className="flex gap-2">
                                            <div className="flex gap-0.5">
                                                <TooltipProvider>
                                                    <div className="flex justify-center w-full gap-1" >
                                                        {(() => {
                                                            if (member.role?.role === 'organization_owner') {
                                                                return (<></>)
                                                            }
                                                            return (
                                                                <>
                                                                    <CheckOrganizationAccess organization_transfer_ownership>
                                                                        <TooltipButton
                                                                            tooltipContent="Transfer an ownership of the organization"
                                                                            disabled={isPendingTransferOrganizationOwnershipMutation}
                                                                            size='action'
                                                                            className={`text-nowrap flex text-sm`}
                                                                            onClick={() => transferOrganizationOwnershipHandler(member.user.id)}
                                                                        >
                                                                            {isPendingTransferOrganizationOwnershipMutation && (<Loader size="16" className="animate-spin" />)}
                                                                            {!isPendingTransferOrganizationOwnershipMutation && (<UserStar size={16} />)}
                                                                        </TooltipButton>
                                                                    </CheckOrganizationAccess>
                                                                    <CheckOrganizationAccess organization_remove_member>
                                                                        <TooltipButton
                                                                            tooltipContent="Remove user from organization"
                                                                            disabled={isPendingRemoveUserMutation}
                                                                            size='action'
                                                                            variant='destructive'
                                                                            className={`text-nowrap flex text-sm`}
                                                                            onClick={() => deleteHandler(member.user.id)}
                                                                        >
                                                                            {isPendingRemoveUserMutation && (<Loader size="16" className="animate-spin" />)}
                                                                            {!isPendingRemoveUserMutation && (<Trash2 size={16} />)}
                                                                        </TooltipButton>
                                                                    </CheckOrganizationAccess>
                                                                </>
                                                            )
                                                        })()}
                                                    </div>
                                                </TooltipProvider>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </DynamicHeightScrollArea>

                    </div>
                </div>
            </div>

            <PageFooter>
                <div className="flex gap-2">
                    <Button size="sm" onClick={() => setShowAddMemberModal(true)}>
                        Add Member
                    </Button>
                </div>
            </PageFooter>
        </>
    )
}